import { useCallback, useState } from "react";
import axios, { AxiosResponse } from "axios";
import toast from "react-hot-toast";
import { CLINIC_TAG_CONFIG_APIS } from "@/api_framework/api_config";
import { DEFAULT_RIBBON_STYLE, HomeTileConfigStatus } from "@/api_framework/api_modals/HomeTileConfig";
import {
  IClinicTagConfig,
  IClinicTagConfigApiRow,
  IClinicTagConfigListData,
  IClinicTagConfigListResponse,
  IClinicTagConfigMutationResponse,
  IClinicTagConfigSaveBody,
  IClinicTagConfigUpdateBody,
} from "@/api_framework/api_modals/ClinicTagConfig";
import { useConfiguration } from "@/app/hooks/useConfiguration";
import { buildAuthHeaders, readErrorMessage, tileUrl } from "./homeTileApiUtils";

const normalizeStatus = (value: string | null | undefined): HomeTileConfigStatus =>
  value?.trim().toLowerCase() === "active" ? "ACTIVE" : "INACTIVE";

const extractRows = (data: IClinicTagConfigListData): IClinicTagConfigApiRow[] => {
  if (!data) return [];
  if (Array.isArray(data)) return data;
  return data.configs ?? data.configurations ?? data.results ?? data.data ?? [];
};

const toClinicTagConfig = (row: IClinicTagConfigApiRow, clinicId: string): IClinicTagConfig => ({
  id: row.config_id ?? row.id ?? "",
  clinic_id: row.clinic_id || clinicId,
  persona_rule: row.configuration?.persona_rule ?? null,
  chip_txt: row.chip_txt ?? "",
  ribbon: {
    bg_color: row.ribbon?.bg_color || DEFAULT_RIBBON_STYLE.bg_color,
    text_color: row.ribbon?.text_color || DEFAULT_RIBBON_STYLE.text_color,
    border_color: row.ribbon?.border_color || DEFAULT_RIBBON_STYLE.border_color,
  },
  status: normalizeStatus(row.status),
  created_at: row.created_at ?? null,
  updated_at: row.updated_at ?? null,
  updated_by: row.updated_by ?? null,
});

export const useClinicTagConfigAPI = () => {
  const configuration = useConfiguration();
  const [configList, setConfigList] = useState<IClinicTagConfig[]>([]);
  const [isListLoading, setIsListLoading] = useState<boolean>(true);
  const [listError, setListError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);

  const accessTokenKey = configuration.localStorage.ACCESS_TOKEN.key;
  const authHeaders = useCallback(() => buildAuthHeaders(accessTokenKey), [accessTokenKey]);

  const fetchClinicConfigs = useCallback(
    async (clinicId: string): Promise<IClinicTagConfig[] | null> => {
      setIsListLoading(true);
      try {
        const res: AxiosResponse<IClinicTagConfigListResponse> = await axios.get(CLINIC_TAG_CONFIG_APIS.CONFIGS, {
          headers: authHeaders(),
          params: { clinic_id: clinicId },
        });
        if (!res.data?.status) {
          setConfigList([]);
          setListError(res.data?.message || "Couldn't load configurations.");
          return null;
        }
        const configs = extractRows(res.data.data)
          .filter((row) => row.status?.trim().toLowerCase() !== "deleted")
          .filter((row) => !row.clinic_id || row.clinic_id === clinicId)
          .map((row) => toClinicTagConfig(row, clinicId))
          .filter((config) => Boolean(config.id));
        setConfigList(configs);
        setListError(null);
        return configs;
      } catch (error) {
        setConfigList([]);
        setListError(readErrorMessage(error) || "Couldn't load configurations.");
        return null;
      } finally {
        setIsListLoading(false);
      }
    },
    [authHeaders],
  );

  const saveConfig = useCallback(
    async (body: IClinicTagConfigSaveBody | IClinicTagConfigUpdateBody, configId?: string): Promise<boolean> => {
      setIsSaving(true);
      try {
        const res: AxiosResponse<IClinicTagConfigMutationResponse> = configId
          ? await axios.put(tileUrl(CLINIC_TAG_CONFIG_APIS.CONFIGS, configId), body, { headers: authHeaders() })
          : await axios.post(CLINIC_TAG_CONFIG_APIS.CONFIGS, body, { headers: authHeaders() });

        if (!res.data?.status) {
          toast.error(res.data?.message || "Failed to save the clinic tag configuration.");
          return false;
        }
        toast.success(configId ? "Clinic tag configuration updated." : "Clinic tag configuration created.");
        return true;
      } catch (error) {
        toast.error(readErrorMessage(error) || "Failed to save the clinic tag configuration. Please try again.");
        return false;
      } finally {
        setIsSaving(false);
      }
    },
    [authHeaders],
  );

  const deleteConfig = useCallback(
    async (configId: string): Promise<boolean> => {
      setIsDeleting(true);
      try {
        const body: IClinicTagConfigUpdateBody = { status: "deleted" };
        const res: AxiosResponse<IClinicTagConfigMutationResponse> = await axios.put(
          tileUrl(CLINIC_TAG_CONFIG_APIS.CONFIGS, configId),
          body,
          { headers: authHeaders() },
        );
        if (!res.data?.status) {
          toast.error(res.data?.message || "Failed to delete the rule.");
          return false;
        }
        setConfigList((current) => current.filter((config) => config.id !== configId));
        toast.success("Rule deleted.");
        return true;
      } catch (error) {
        toast.error(readErrorMessage(error) || "Failed to delete the rule. Please try again.");
        return false;
      } finally {
        setIsDeleting(false);
      }
    },
    [authHeaders],
  );

  return {
    configList,
    isListLoading,
    listError,
    isSaving,
    isDeleting,
    fetchClinicConfigs,
    saveConfig,
    deleteConfig,
  };
};
